import React, { useState, useEffect } from "react";
import HTMLFlipBook from "react-pageflip";
import './styles.scss';
import { updateHeader } from "../../slices/header-slice";
import { useDispatch } from "react-redux";
import { getLookBookImages } from "../../api";

// Page component for lazy loading images
const Page = React.forwardRef(({ image, pageNumber, width, height }, ref) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        if (image?.fullUrl) {
            const img = new Image();
            img.onload = () => setImageLoaded(true);
            img.onerror = () => setImageError(true);
            img.src = image.fullUrl;
        }
    }, [image]);

    return (
        <div ref={ref} className="lookbook-page" style={{ width, height }}>
            {imageLoaded ? (
                <img
                    src={image?.fullUrl}
                    alt={`Page ${pageNumber}`}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
            ) : imageError ? (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                }}>
                    <div>Image not found</div>
                </div>
            ) : (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                }}>
                    <div>Loading...</div>
                </div>
            )}
        </div>
    );
});

export default function LookBook({ headerOffset = 130 }) {
    const [isLoading, setIsLoading] = useState(true);
    const [images, setImages] = useState([]);
    const [error, setError] = useState(null);
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    // Check if device is mobile (768px or less)
    const isMobile = windowSize.width <= 768;

    // Calculate responsive dimensions
    const availableWidth = windowSize.width;
    const availableHeight = windowSize.height - headerOffset;

    // US Letter aspect ratio (8.5 x 11)
    const US_LETTER_RATIO = 8.5 / 11; // ≈ 0.773

    // Calculate book dimensions - maximize height usage while staying above the fold
    const maxBookHeight = availableHeight * 0.95; // Use 95% of available height
    const bookWidthFromHeight = maxBookHeight * US_LETTER_RATIO;

    // Use the height-constrained width, but ensure it doesn't exceed screen width
    const maxBookWidth = availableWidth * 0.95; // Use 95% of available width as absolute max
    let bookWidth = Math.min(bookWidthFromHeight, maxBookWidth);

    // Apply absolute limits
    bookWidth = Math.max(Math.min(bookWidth, 1920), 300);

    const bookHeight = bookWidth / US_LETTER_RATIO;

    const dispatch = useDispatch();


    // Fetch look book images from API
    useEffect(() => {
        dispatch(updateHeader('light'));

        const fetchImages = async () => {
            try {
                setIsLoading(true);
                const response = await getLookBookImages();

                // Sort images by order
                const sortedImages = response.data.images.sort((a, b) => a.order - b.order);
                setImages(sortedImages);
                setError(null);
            } catch (err) {
                console.error('Error fetching look book images:', err);
                setError('Failed to load look book images. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchImages();
    }, [dispatch]);

    // Handle window resize to force re-render
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    // Handle PDF download
    const handleDownloadPDF = () => {
        const link = document.createElement('a');
        link.href = '/pdfs/Paramount Wood Co Portfolio 2026.pdf';
        link.download = 'Paramount Wood Co Portfolio 2026.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Mobile image gallery component
    const MobileImageGallery = () => (
        <div className="mobile-gallery">
            <div className="gallery-header">
                <h2>Portfolio 2026</h2>
                <p>Swipe or scroll to view all pages</p>

                <button className="cta gold-fill" onClick={handleDownloadPDF}>Download PDF Portfolio</button>
            </div>
            <div className="gallery-images">
                {images.map((image, index) => (
                    <div key={image.fileName || index} className="gallery-image-container">
                        <img
                            src={image.fullUrl}
                            alt={`Portfolio page ${index + 1}`}
                            className="gallery-image"
                            loading="lazy"
                        />
                        <div className="page-number">Page {index + 1}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="lookbook-jpg-wrapper">
            <div style={{ height: headerOffset }} /> {/* spacer for header */}

            <div className="lookbook-jpg-holder">
                <div className="lookbook-jpg-center">
                    {isLoading ? (
                        <div className="lookbook-loading">
                            <h2>Loading Look Book...</h2>
                            <p>Please wait while we load the images...</p>
                        </div>
                    ) : error ? (
                        <div className="lookbook-error">
                            <h2>Error Loading Look Book</h2>
                            <p>{error}</p>
                        </div>
                    ) : images.length === 0 ? (
                        <div className="lookbook-loading">
                            <h2>No Images Available</h2>
                            <p>The look book has no images to display.</p>
                        </div>
                    ) : isMobile ? (
                        <MobileImageGallery />
                    ) : (
                        <HTMLFlipBook
                            key={`${bookWidth}-${bookHeight}`}
                            width={bookWidth}
                            height={bookHeight}
                            size="fixed"
                            showCover={true}
                            className="lookbook-jpg-book"
                            drawShadow={true}
                            maxShadowOpacity={0.2}
                            usePortrait={false}
                            mobileScrollSupport={true}
                            flippingTime={1000}
                            autoSize={false}
                        >
                            {images.map((image, index) => (
                                <Page
                                    key={image.fileName || index}
                                    image={image}
                                    pageNumber={index + 1}
                                    width={bookWidth}
                                    height={bookHeight}
                                />
                            ))}
                        </HTMLFlipBook>
                    )}
                </div>
            </div>

            {!isMobile && !isLoading && !error && images.length > 0 && (
                <button className="cta gold-fill" onClick={handleDownloadPDF}>Download PDF Portfolio</button>
            )}
        </div>
    );
}
